import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client/react';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { ReviewTypeDetails } from './review-type-details';
import EditReviewPlanStep1 from './step-1';
import EditReviewPlanStep2 from './step-2';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { ReviewPlanDetailDto, ReviewPlanUpdateDto } from '@interfaces-clean/review-plan.interface';
import { useLocalState, useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { GET_DATA_REVIEW_TABLE_ALL_COLUMNS } from '@queries/central-monitoring';
import {
    dataReviewTableAllColumns,
    dataReviewTableAllColumnsVariables,
} from '@queries/generated/central-monitoring.generated';
import {
    dataListingsTablesForStudy,
    dataListingsTablesForStudyVariables,
    getSubjectMonitoringConfig,
    getSubjectMonitoringConfigVariables,
} from '@queries/generated/subject-monitoring.generated';
import { GET_DATA_LISTINGS_TABLES_FOR_STUDY, GET_SUBJECT_MONITORING_CONFIGURATION } from '@queries/subject-monitoring';

const COMPONENT_NAME = 'edit-review-plan-dialog';
const STEP_COUNT = 2;

interface EditFormValues {
    active: boolean;
    reviewSubject: boolean;
}

interface ListingOption {
    text: string;
    value: string;
}

export interface EditReviewPlanDialogProps {
    reviewPlan: ReviewPlanDetailDto;
    onClose: () => void;
    onSubmit: (dto: ReviewPlanUpdateDto) => Promise<void>;
}

const EditReviewPlanDialog = ({ reviewPlan, onClose, onSubmit }: EditReviewPlanDialogProps) => {
    const { t } = useTranslation();
    const { state } = useLocalState();
    const studyRef = state?.selectedStudy?.id as string | undefined;
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges, discardChanges } =
        useUnsavedChangesContext();

    const [currentPage, setCurrentPage] = useState(1);

    // --- Queries ---

    const { data: configData, loading: configLoading } = useQuery<
        getSubjectMonitoringConfig,
        getSubjectMonitoringConfigVariables
    >(GET_SUBJECT_MONITORING_CONFIGURATION, { variables: { studyId: studyRef }, skip: !studyRef });

    const datasetTimestamp = state?.selectedDatasetDate?.created ?? undefined;

    const { data: listingsData, loading: listingsLoading } = useQuery<
        dataListingsTablesForStudy,
        dataListingsTablesForStudyVariables
    >(GET_DATA_LISTINGS_TABLES_FOR_STUDY, {
        variables: { studyId: studyRef, timestamp: datasetTimestamp },
        skip: !studyRef,
    });

    const listingsLoaded = !configLoading && !listingsLoading && !!configData && !!listingsData;

    // --- Step 1: data listing pool ---

    const allListings = useMemo(() => {
        if (!listingsLoaded) return null;

        const configuredItems = configData?.dataListingsConfigTableForStudy?.dataListingsItems ?? [];
        const allStudyListings = listingsData?.dataListingsForStudy ?? [];

        const isActive = (id: string) => allStudyListings.find((x) => x?.id === id)?.active ?? false;

        const includedIds = new Set(
            (reviewPlan.availableDataListings ?? []).filter((d) => d.included).map((d) => d.dataListingId ?? ''),
        );
        const configuredIds = new Set(configuredItems.map((item) => item?.datatableId ?? ''));

        const activeConfigured = configuredItems
            .filter((item) => item && isActive(item.datatableId ?? ''))
            .map((item) => {
                const listing = allStudyListings.find((x) => x?.id === item!.datatableId);
                return {
                    dataListingId: item!.datatableId ?? '',
                    name: listing?.name ?? item!.name ?? '',
                    isActive: true,
                    included: includedIds.has(item!.datatableId ?? ''),
                };
            });

        const inactiveIncluded = (reviewPlan.availableDataListings ?? [])
            .filter(
                (d) => d.included && (!configuredIds.has(d.dataListingId ?? '') || !isActive(d.dataListingId ?? '')),
            )
            .map((d) => ({
                dataListingId: d.dataListingId ?? '',
                name: d.name ?? '',
                isActive: false,
                included: true,
            }));

        return [...activeConfigured, ...inactiveIncluded].sort((a, b) => a.name.localeCompare(b.name));
    }, [listingsLoaded, configData, listingsData, reviewPlan]);

    const hasInitializedListingsRef = useRef(false);
    const [includedListings, setIncludedListings] = useState<DragDropListItemProps[]>([]);
    const [availableListings, setAvailableListings] = useState<DragDropListItemProps[]>([]);
    const [initialIncludedIds, setInitialIncludedIds] = useState(new Set<string>());

    useEffect(() => {
        if (hasInitializedListingsRef.current || !allListings) return;
        hasInitializedListingsRef.current = true;

        const included = allListings.filter((d) => d.included);
        const available = allListings.filter((d) => !d.included && d.isActive);

        setIncludedListings(
            included.map((d) => ({ dataListingId: d.dataListingId, title: d.name, active: d.isActive })),
        );
        setAvailableListings(available.map((d) => ({ dataListingId: d.dataListingId, title: d.name, active: true })));
        setInitialIncludedIds(new Set(included.map((d) => d.dataListingId)));
    }, [allListings]);

    // --- Step 2: columns per listing ---

    const [selectedListingId, setSelectedListingId] = useState<string | null>(null);

    // The plan's originally-saved watched dataFields per listing - the immutable baseline used both
    // to seed each listing's column lists on first visit, and to detect changes on submit.
    const [initialWatchedColumnIds] = useState<Record<string, string[]>>(() =>
        Object.fromEntries(
            (reviewPlan.availableDataListings ?? [])
                .filter((d) => d.included)
                .map((d) => [d.dataListingId ?? '', d.columns ?? []]),
        ),
    );

    // Per-listing Watched/Available column item lists, mirroring how step 1 holds includedListings /
    // availableListings as persistent state. Lazily populated the first time a listing is selected
    // (see the effect below) and from then on evolved purely via onChangeItemsLeft/onChangeItemsRight -
    // never re-derived from the live query - so a column moved between lists is never lost, regardless
    // of whether it's still active on the Data Listing table.
    const [columnItemsByListing, setColumnItemsByListing] = useState<
        Record<string, { watched: DragDropListItemProps[]; available: DragDropListItemProps[] }>
    >({});
    const initializedListingIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (currentPage !== 2) return;
        if (includedListings.length === 0) {
            setSelectedListingId(null);
            return;
        }
        const stillExists = includedListings.some((l) => l.dataListingId === selectedListingId);
        if (!stillExists) {
            const firstSorted = [...includedListings].sort((a, b) =>
                (a.title as string).localeCompare(b.title as string),
            )[0];
            setSelectedListingId(firstSorted.dataListingId as string);
        }
    }, [currentPage, selectedListingId, includedListings]);

    // Deliberately not scoped to datasetTimestamp (the app's globally-selected dataset elsewhere in
    // the nav) - this is an admin config screen, so Active must reflect the table's true current
    // state. Pinning to a dataset snapshot would make Active/inactive depend on which dataset
    // happens to be selected (and on datasetTimestamp's load timing), producing inconsistent results.
    const { data: allColumnsData, loading: allColumnsLoading } = useQuery<
        dataReviewTableAllColumns,
        dataReviewTableAllColumnsVariables
    >(GET_DATA_REVIEW_TABLE_ALL_COLUMNS, {
        variables: { id: selectedListingId ?? '' },
        skip: !selectedListingId || currentPage !== 2,
    });

    useEffect(() => {
        // Wait for the query to fully settle (not just for `data` to be truthy) before locking in -
        // with the app's cache-and-network fetch policy, `data` can briefly reflect an incomplete
        // cache entry for this table (e.g. cached elsewhere without AllColumns) before the real
        // network response lands. Since a listing is only ever initialised once, latching onto that
        // transient state would permanently miss Active flags until the dialog was reopened.
        if (!selectedListingId || allColumnsLoading || !allColumnsData?.dataReviewTable) return;
        // Guard against the response belonging to a since-superseded query for a different listing
        // (e.g. a slow in-flight request resolving after the user already switched listings again).
        if (String(allColumnsData.dataReviewTable.id) !== selectedListingId) return;
        if (initializedListingIdsRef.current.has(selectedListingId)) return;
        initializedListingIdsRef.current.add(selectedListingId);

        const watchedSet = new Set(initialWatchedColumnIds[selectedListingId] ?? []);
        const allColumns = (allColumnsData.dataReviewTable.allColumns ?? []).filter(Boolean);

        const toItem = (c: (typeof allColumns)[number]): DragDropListItemProps => ({
            columnId: String(c!.columnId ?? ''),
            dataField: String(c!.dataField ?? ''),
            title: c!.heading ?? '',
            active: c!.active ?? true,
        });

        // Only surface a column if it's still active on the table, or it's currently on watch (an
        // inactive, never-watched column is just noise) - the same rule step 1 applies to listings.
        const relevantColumns = allColumns.filter((c) => c!.active || watchedSet.has(String(c!.dataField ?? '')));

        setColumnItemsByListing((prev) => ({
            ...prev,
            [selectedListingId]: {
                watched: relevantColumns.filter((c) => watchedSet.has(String(c!.dataField ?? ''))).map(toItem),
                available: relevantColumns.filter((c) => !watchedSet.has(String(c!.dataField ?? ''))).map(toItem),
            },
        }));
    }, [selectedListingId, allColumnsData, allColumnsLoading, initialWatchedColumnIds]);

    // Watched dataFields for a listing - from live per-listing state once loaded, otherwise the
    // plan's original baseline (a listing the user never opens in step 2 is by definition unchanged).
    // A watched column flagged inactive (no longer on the Data Listing table) is dropped here even
    // if the user never manually drags it out - the inactive icon's tooltip promises it's removed
    // on save, so it must actually be excluded from what gets submitted.
    const watchedColumnIdsForListing = (dataListingId: string): string[] =>
        columnItemsByListing[dataListingId]
            ? columnItemsByListing[dataListingId].watched
                  .filter((i) => i.active !== false)
                  .map((i) => String(i.dataField ?? ''))
            : (initialWatchedColumnIds[dataListingId] ?? []);

    const watchedColumnItems = selectedListingId ? (columnItemsByListing[selectedListingId]?.watched ?? []) : [];
    const availableColumnItems = selectedListingId ? (columnItemsByListing[selectedListingId]?.available ?? []) : [];

    // --- Form ---

    const {
        control,
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = useForm<EditFormValues>({
        mode: 'all',
        defaultValues: {
            active: reviewPlan.active ?? true,
            reviewSubject: reviewPlan.reviewSubject ?? false,
        },
    });

    // --- Dirty detection ---

    const hasListingChanges = useMemo(() => {
        const current = includedListings.map((i) => i.dataListingId as string);
        if (current.length !== initialIncludedIds.size) return true;
        return current.some((id) => !initialIncludedIds.has(id));
    }, [includedListings, initialIncludedIds]);

    // Whether saving now would actually change the persisted columns for a listing - includes a
    // stale column sitting untouched in the Watched list, since that gets silently dropped on
    // submit regardless of any manual drag (see watchedColumnIdsForListing). Drives the Save button.
    const hasColumnChangesToSave = useMemo(() => {
        return includedListings.some((item) => {
            const id = item.dataListingId as string;
            const cols = columnItemsByListing[id]
                ? columnItemsByListing[id].watched
                      .filter((i) => i.active !== false)
                      .map((i) => String(i.dataField ?? ''))
                : (initialWatchedColumnIds[id] ?? []);
            const initialSet = new Set(initialWatchedColumnIds[id] ?? []);
            if (cols.length !== initialSet.size) return true;
            return cols.some((c) => !initialSet.has(c));
        });
    }, [includedListings, columnItemsByListing, initialWatchedColumnIds]);

    // Whether the user has actually edited a listing's columns (dragged something between Watched/
    // Available) - deliberately unfiltered, so a stale column that's merely being displayed (and
    // will be auto-dropped on save) does NOT count as something the user would lose by closing.
    // Drives the unsaved-changes discard prompt on Cancel/close.
    const hasUserMadeColumnChanges = useMemo(() => {
        return includedListings.some((item) => {
            const id = item.dataListingId as string;
            const cols = columnItemsByListing[id]
                ? columnItemsByListing[id].watched.map((i) => String(i.dataField ?? ''))
                : (initialWatchedColumnIds[id] ?? []);
            const initialSet = new Set(initialWatchedColumnIds[id] ?? []);
            if (cols.length !== initialSet.size) return true;
            return cols.some((c) => !initialSet.has(c));
        });
    }, [includedListings, columnItemsByListing, initialWatchedColumnIds]);

    const hasChanges = isDirty || hasListingChanges || hasColumnChangesToSave;
    const hasUnsavedUserChanges = isDirty || hasListingChanges || hasUserMadeColumnChanges;

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, hasUnsavedUserChanges);

        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, [setComponentHasChanges, hasUnsavedUserChanges]);

    // --- Handlers ---

    const onCloseHandler = async () => {
        if (componentHasChanges(COMPONENT_NAME)) {
            const confirmed = await confirmDiscardChanges(COMPONENT_NAME);
            if (!confirmed) return;
            discardChanges();
        }
        onClose();
    };

    const onSubmitHandler = async (data: EditFormValues) => {
        await onSubmit({
            active: data.active,
            reviewSubject: data.reviewSubject,
            dataListings: includedListings.map((item) => {
                const dataListingId = item.dataListingId as string;
                return { dataListingId, columns: watchedColumnIdsForListing(dataListingId) };
            }),
        });
    };

    const handleStepAction = (event: { action: string }) => {
        if (event.action === 'back') {
            setSelectedListingId(null);
            setCurrentPage((p) => Math.max(1, p - 1));
        } else if (event.action === 'next') {
            setCurrentPage((p) => Math.min(STEP_COUNT, p + 1));
        }
    };

    const handleListingSelect = (e: DropDownListChangeEvent) => {
        const option = e.value as ListingOption;
        setSelectedListingId(option?.value ?? null);
    };

    const handleWatchedColumnsChange = (items: DragDropListItemProps[]) => {
        if (!selectedListingId) return;
        setColumnItemsByListing((prev) => ({
            ...prev,
            [selectedListingId]: { ...prev[selectedListingId], watched: items },
        }));
    };

    const handleAvailableColumnsChange = (items: DragDropListItemProps[]) => {
        if (!selectedListingId) return;
        setColumnItemsByListing((prev) => ({
            ...prev,
            [selectedListingId]: { ...prev[selectedListingId], available: items },
        }));
    };

    // --- Render ---

    const listingOptions: ListingOption[] = includedListings
        .map((item) => ({ text: item.title as string, value: item.dataListingId as string }))
        .sort((a, b) => a.text.localeCompare(b.text));

    const selectedOption = listingOptions.find((o) => o.value === selectedListingId) ?? null;

    const stepTitle = t(`admin:smReviewPlans.editDialog.steps.${currentPage}.title`);

    const noDataListingsConfigured = listingsLoaded && allListings !== null && allListings.length === 0;

    const planCreatedAudit =
        reviewPlan.createdBy && reviewPlan.createdOn
            ? `${stringDateWithoutTimeFromUtc(reviewPlan.createdOn, 'DD-MMM-YYYY')} by ${reviewPlan.createdBy}`
            : undefined;
    const planUpdatedAudit =
        reviewPlan.updatedBy && reviewPlan.updatedOn
            ? `${stringDateWithoutTimeFromUtc(reviewPlan.updatedOn, 'DD-MMM-YYYY')} by ${reviewPlan.updatedBy}`
            : undefined;

    const reviewTypeCreatedAudit =
        reviewPlan.reviewTypeCreatedBy && reviewPlan.reviewTypeCreatedOn
            ? `${stringDateWithoutTimeFromUtc(reviewPlan.reviewTypeCreatedOn, 'DD-MMM-YYYY')} by ${reviewPlan.reviewTypeCreatedBy}`
            : undefined;
    const reviewTypeUpdatedAudit =
        reviewPlan.reviewTypeUpdatedBy && reviewPlan.reviewTypeUpdatedOn
            ? `${stringDateWithoutTimeFromUtc(reviewPlan.reviewTypeUpdatedOn, 'DD-MMM-YYYY')} by ${reviewPlan.reviewTypeUpdatedBy}`
            : undefined;

    const dialogTitle = `${t('admin:smReviewPlans.editDialog.title')}: ${reviewPlan.reviewTypeName ?? ''}`;
    // At 70 chars (max title length) that's around 1300px, but we don't need it that wide unless we need it to be
    // So keep it at the 900px min width unless the title is wider than that
    const minWidth = Math.max(900, dialogTitle.length * 14);

    return (
        <DraggableDialog
            title={dialogTitle}
            dataTestId="edit-review-plan-dialog"
            primaryBtnLabel={t('common:save')}
            primaryBtnDisabled={!hasChanges || isSubmitting}
            secondaryBtnLabel={t('common:cancel')}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmitHandler)().catch((err) => window.console.error(err))}
            stepCount={STEP_COUNT}
            currentStep={currentPage}
            currentStepTitle={stepTitle}
            onStepAction={handleStepAction}
            initialWidth={minWidth}
            initialHeight={620}
            bodyPadding="none"
            additionalInfo={
                <ReviewTypeDetails
                    reviewTypeName={reviewPlan.reviewTypeName}
                    reviewTypeDescription={reviewPlan.reviewTypeDescription}
                    createdAudit={reviewTypeCreatedAudit}
                    updatedAudit={reviewTypeUpdatedAudit}
                />
            }
            additionalInfoWidth={320}
            additionalInfoBtnLabel={t('admin:smReviewPlans.editDialog.reviewTypeDetails')}
            createdUpdatedInfo={
                planCreatedAudit || planUpdatedAudit ? (
                    <CreatedUpdatedInfo created={planCreatedAudit} updated={planUpdatedAudit} />
                ) : undefined
            }
            createdUpdatedBackgroundColour="gray"
            setHeight="100%"
        >
            <>
                {listingsLoaded && currentPage === 1 && (
                    <EditReviewPlanStep1
                        control={control}
                        includedListings={includedListings}
                        availableListings={availableListings}
                        onIncludedChange={setIncludedListings}
                        onAvailableChange={setAvailableListings}
                        noDataListingsConfigured={noDataListingsConfigured}
                    />
                )}
                {currentPage === 2 && !(allColumnsLoading && !columnItemsByListing[selectedListingId ?? '']) && (
                    <EditReviewPlanStep2
                        listingOptions={listingOptions}
                        selectedOption={selectedOption}
                        onListingSelect={handleListingSelect}
                        watchedColumnItems={watchedColumnItems}
                        availableColumnItems={availableColumnItems}
                        onWatchedColumnsChange={handleWatchedColumnsChange}
                        onAvailableColumnsChange={handleAvailableColumnsChange}
                        noDataListingsConfigured={noDataListingsConfigured}
                    />
                )}
            </>
        </DraggableDialog>
    );
};

export default EditReviewPlanDialog;
